import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { ChartLine, Eye, EyeSlash } from '@phosphor-icons/react';
import { useRatingsHistory, useRatingsHistoryMulti, type Platform } from '@/api/ratings';
import type { RatingHistoryPoint } from './types';
import { buildPath, downsamplePoints, sinceFromDays, tsOf } from './eloHistory.utils';

/**
 * EloHistoryChart — courbes ELO historiques (T5 P1 Stats Hub puis PR #G Stats Hub V2).
 *
 * Deux modes :
 *  - **multi-plateforme** (défaut) : 1 cadence (Classique/Rapide/Blitz) sélectionnée
 *    via onglets, 3 courbes (FIDE + Lichess + chess.com) superposées. Mode legacy
 *    consommé par `<StatsPage>` onglet "Vue globale".
 *  - **multi-cadence** (`multiCadence={true}`) : 1 plateforme fixe, N courbes
 *    (Std + Rapide + Blitz, + Bullet/Puzzle/Storm selon plateforme) superposées
 *    sur le même graphe. Sert le nouvel onglet "Performance" (PR #H Stats Hub V2)
 *    qui pousse une vue cross-cadence pour repérer corrélations.
 *
 * SVG natif (pas de lib externe — cohérent `<Sparkline>` ui/telemetry.tsx). Respecte
 * le design system :
 *  - dark mode only, tabular-nums, Phosphor icons uniquement
 *  - palette multi-plateforme : FIDE=paper-50, Lichess=violet-500, chess.com=amber-500
 *  - palette multi-cadence : standard/classical=paper-50, rapide/rapid=acid-500,
 *    blitz=violet-500, bullet=cyan-500, puzzle/puzzleRush=volt-500, storm=coral-500,
 *    tactics=volt-500, daily=amber-500
 *  - no emoji, no exclamation mark, no flattery
 *
 * Cadence mapping (mode multi-plateforme) :
 *  - Classique : FIDE 'standard' / Lichess 'classical' / chess.com absent
 *  - Rapide    : FIDE 'rapide'   / Lichess 'rapid'     / chess.com 'rapid'
 *  - Blitz     : FIDE 'blitz'    / Lichess 'blitz'     / chess.com 'blitz'
 *
 * Downsampling : si > 365 points par série on prend 1 point tous les
 * `ceil(N/365)` éléments (cf. ROADMAP §risque 4).
 *
 * Lissage : prop `smoothed` (défaut `false`) pose la valeur initiale du toggle
 * "Lissage" — l'utilisateur peut ensuite l'inverser via la checkbox.
 */

export type CadenceTab = 'classique' | 'rapide' | 'blitz';

const CADENCE_LABELS: Record<CadenceTab, string> = {
  classique: 'Classique',
  rapide: 'Rapide',
  blitz: 'Blitz',
};

interface PlatformConfig {
  id: 'fide' | 'lichess' | 'chesscom';
  label: string;
  color: string;
}

const PLATFORMS: ReadonlyArray<PlatformConfig> = [
  { id: 'fide', label: 'FIDE', color: 'var(--paper-50)' },
  { id: 'lichess', label: 'Lichess', color: 'var(--violet-500)' },
  { id: 'chesscom', label: 'chess.com', color: 'var(--amber-500)' },
];

const RANGES: ReadonlyArray<{ id: string; label: string; days: number | null }> = [
  { id: '30d', label: '30j', days: 30 },
  { id: '90d', label: '90j', days: 90 },
  { id: '1y', label: '1an', days: 365 },
  { id: 'all', label: 'Tout', days: null },
];

const CADENCE_MAP: Record<CadenceTab, Record<'fide' | 'lichess' | 'chesscom', string | null>> = {
  classique: { fide: 'standard', lichess: 'classical', chesscom: null },
  rapide: { fide: 'rapide', lichess: 'rapid', chesscom: 'rapid' },
  blitz: { fide: 'blitz', lichess: 'blitz', chesscom: 'blitz' },
};

/**
 * Couleur par cadence (mode multi-cadence). Cohérent DESIGN.md §2 :
 * - tokens CSS uniquement (pas d'hex en dur dans le JSX)
 * - max 2 accents simultanés respecté par la sélection naturelle (l'utilisateur
 *   masque par toggle de légende les courbes qui le bruitent)
 */
const CADENCE_COLOR: Record<string, string> = {
  standard: 'var(--paper-50)',
  classical: 'var(--paper-50)',
  rapide: 'var(--acid-500)',
  rapid: 'var(--acid-500)',
  blitz: 'var(--violet-500)',
  bullet: 'var(--cyan-500)',
  puzzle: 'var(--volt-500)',
  puzzleRush: 'var(--volt-500)',
  storm: 'var(--coral-500)',
  tactics: 'var(--volt-500)',
  daily: 'var(--amber-500)',
};

/**
 * Libellé affiché par cadence. On garde un mapping centralisé pour éviter les
 * littéraux dispersés dans le JSX (cf. CONVENTIONS §3.1 — module auto-contenu).
 */
const CADENCE_LABEL: Record<string, string> = {
  standard: 'Standard',
  classical: 'Classique',
  rapide: 'Rapide',
  rapid: 'Rapide',
  blitz: 'Blitz',
  bullet: 'Bullet',
  puzzle: 'Puzzle',
  puzzleRush: 'Puzzle Rush',
  storm: 'Storm',
  tactics: 'Tactics',
  daily: 'Daily',
};

export interface EloHistoryChartProps {
  cadenceOptions?: ReadonlyArray<CadenceTab>;
  defaultCadence?: CadenceTab;
  /** Hauteur SVG (px). Largeur 100% du conteneur via viewBox. */
  height?: number;
  /**
   * Active le mode multi-cadence simultanée. Quand `true`, le composant
   * affiche toutes les cadences de la plateforme sélectionnée sur le même
   * graphe (au lieu des 3 plateformes pour 1 cadence). Défaut : `false`.
   */
  multiCadence?: boolean;
  /** Plateforme à afficher en mode multi-cadence. Défaut : `'lichess'`. */
  platform?: Platform;
  /** Valeur initiale du toggle "Lissage" (l'utilisateur peut l'inverser). */
  smoothed?: boolean;
}

interface MultiPlatformSeries {
  platform: PlatformConfig;
  points: RatingHistoryPoint[];
  isLoading: boolean;
  isError: boolean;
  available: boolean;
}

interface MultiCadenceSeries {
  cadence: string;
  label: string;
  color: string;
  points: RatingHistoryPoint[];
}

interface TooltipState {
  x: number;
  y: number;
  date: string;
  value: number;
  seriesLabel: string;
  color: string;
}

export function EloHistoryChart({
  cadenceOptions = ['classique', 'rapide', 'blitz'],
  defaultCadence,
  height = 240,
  multiCadence = false,
  platform = 'lichess',
  smoothed = false,
}: EloHistoryChartProps): JSX.Element {
  if (multiCadence) {
    return (
      <EloMultiCadenceView platform={platform} height={height} smoothedInitial={smoothed} />
    );
  }
  return (
    <EloMultiPlatformView
      cadenceOptions={cadenceOptions}
      defaultCadence={defaultCadence}
      height={height}
      smoothedInitial={smoothed}
    />
  );
}

// ===========================================================================
// Vue multi-plateforme (mode historique — 3 courbes, 1 cadence sélectionnable)
// ===========================================================================

interface MultiPlatformViewProps {
  cadenceOptions: ReadonlyArray<CadenceTab>;
  defaultCadence: CadenceTab | undefined;
  height: number;
  smoothedInitial: boolean;
}

function EloMultiPlatformView({
  cadenceOptions,
  defaultCadence,
  height,
  smoothedInitial,
}: MultiPlatformViewProps): JSX.Element {
  const initialCadence: CadenceTab =
    defaultCadence && cadenceOptions.includes(defaultCadence)
      ? defaultCadence
      : (cadenceOptions[0] ?? 'classique');
  const [cadence, setCadence] = useState<CadenceTab>(initialCadence);
  const [rangeId, setRangeId] = useState<string>('1y');
  const [smooth, setSmooth] = useState<boolean>(smoothedInitial);
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const range = RANGES.find((r) => r.id === rangeId) ?? RANGES[2]!;
  const since = sinceFromDays(range.days);

  // 3 hooks fixes (un par plateforme) pour respecter Rules of Hooks.
  const fideCad = CADENCE_MAP[cadence].fide;
  const lichessCad = CADENCE_MAP[cadence].lichess;
  const chesscomCad = CADENCE_MAP[cadence].chesscom;
  const fideQ = useRatingsHistory('fide', fideCad ?? 'standard', since);
  const lichessQ = useRatingsHistory('lichess', lichessCad ?? 'rapid', since);
  const chesscomQ = useRatingsHistory('chesscom', chesscomCad ?? 'rapid', since);

  const series: MultiPlatformSeries[] = useMemo(() => {
    const queries = [
      { platform: PLATFORMS[0]!, q: fideQ, available: fideCad !== null },
      { platform: PLATFORMS[1]!, q: lichessQ, available: lichessCad !== null },
      { platform: PLATFORMS[2]!, q: chesscomQ, available: chesscomCad !== null },
    ];
    return queries.map(({ platform: p, q, available }) => ({
      platform: p,
      points: available && q.data?.points ? downsamplePoints(q.data.points) : [],
      isLoading: available ? q.isLoading : false,
      isError: available ? q.isError : false,
      available,
    }));
  }, [fideQ, lichessQ, chesscomQ, fideCad, lichessCad, chesscomCad]);

  const visibleSeries = series.filter(
    (s) => s.available && !hidden[s.platform.id] && s.points.length > 0,
  );
  const anyLoading = series.some((s) => s.isLoading);
  const anyError = series.some((s) => s.isError);

  function toggleSeries(id: string): void {
    setHidden((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  const flatPoints = visibleSeries.flatMap((s) =>
    s.points.map((p) => ({
      ts: tsOf(p.rated_at),
      value: p.value,
      label: s.platform.label,
      color: s.platform.color,
    })),
  );

  return (
    <ChartShell
      title="Historique ELO"
      ariaLabel={`Courbes ELO ${CADENCE_LABELS[cadence]}`}
      height={height}
      headerControls={
        <div
          className="flex gap-1 p-1 rounded-lg border border-ink-700 bg-ink-900"
          role="tablist"
          aria-label="Cadence"
        >
          {cadenceOptions.map((c) => {
            const active = c === cadence;
            return (
              <button
                key={c}
                role="tab"
                aria-selected={active}
                onClick={() => setCadence(c)}
                className={`px-3 py-1 text-body-sm font-mono uppercase tracking-wider rounded-md transition-colors ${
                  active
                    ? 'bg-acid-500 text-ink-900'
                    : 'text-ink-300 hover:text-paper-50'
                }`}
              >
                {CADENCE_LABELS[c]}
              </button>
            );
          })}
        </div>
      }
      legend={
        <div className="flex gap-3 mb-3 flex-wrap" data-testid="elo-history-legend">
          {series.map((s) => {
            const isHidden = !!hidden[s.platform.id];
            const isDisabled = !s.available;
            return (
              <LegendButton
                key={s.platform.id}
                id={s.platform.id}
                label={s.platform.label}
                color={s.platform.color}
                hidden={isHidden}
                disabled={isDisabled}
                onToggle={() => toggleSeries(s.platform.id)}
                testId={`legend-${s.platform.id}`}
              />
            );
          })}
        </div>
      }
      visibleSeriesIds={visibleSeries.map((s) => s.platform.id)}
      seriesForSvg={visibleSeries.map((s) => ({
        id: s.platform.id,
        color: s.platform.color,
        points: s.points,
        testId: `series-${s.platform.id}`,
      }))}
      flatPoints={flatPoints}
      smooth={smooth}
      setSmooth={setSmooth}
      rangeId={rangeId}
      setRangeId={setRangeId}
      tooltip={tooltip}
      setTooltip={setTooltip}
      anyLoading={anyLoading}
      anyError={anyError}
    />
  );
}

// ===========================================================================
// Vue multi-cadence (PR #G — N cadences pour 1 plateforme)
// ===========================================================================

interface MultiCadenceViewProps {
  platform: Platform;
  height: number;
  smoothedInitial: boolean;
}

function EloMultiCadenceView({
  platform,
  height,
  smoothedInitial,
}: MultiCadenceViewProps): JSX.Element {
  const [rangeId, setRangeId] = useState<string>('1y');
  const [smooth, setSmooth] = useState<boolean>(smoothedInitial);
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const range = RANGES.find((r) => r.id === rangeId) ?? RANGES[2]!;
  const since = sinceFromDays(range.days);
  const historyQ = useRatingsHistoryMulti(platform, since);

  /**
   * Pivote la réponse `{rated_at, std?, rapide?, blitz?, ...}` en N séries
   * `{cadence, points: [{rated_at, value}, ...]}`. Une cadence sans aucun
   * point est exclue de la légende (cohérent UX : pas de bouton mort).
   *
   * Dépendance unique : `historyQ.data` (la réponse). Ainsi on évite que
   * `cadences`/`rawPoints` recréés à chaque render ne déclenchent un recompute
   * inutile (cf. eslint react-hooks/exhaustive-deps).
   */
  const series: MultiCadenceSeries[] = useMemo(() => {
    const cadences = historyQ.data?.cadences ?? [];
    const rawPoints = historyQ.data?.points ?? [];
    return cadences.map((cad) => {
      const points: RatingHistoryPoint[] = [];
      for (const row of rawPoints) {
        // Accès dynamique par clé cadence ; on passe par `unknown` pour
        // satisfaire `noUncheckedIndexedAccess` sans relâcher la sûreté.
        const raw = (row as unknown as Record<string, unknown>)[cad];
        if (typeof raw === 'number') {
          points.push({ rated_at: row.rated_at, value: raw });
        }
      }
      return {
        cadence: cad,
        label: CADENCE_LABEL[cad] ?? cad,
        color: CADENCE_COLOR[cad] ?? 'var(--paper-50)',
        points: downsamplePoints(points),
      };
    });
  }, [historyQ.data]);

  const visibleSeries = series.filter((s) => !hidden[s.cadence] && s.points.length > 0);
  const anyLoading = historyQ.isLoading;
  const anyError = historyQ.isError;

  function toggleSeries(id: string): void {
    setHidden((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  const flatPoints = visibleSeries.flatMap((s) =>
    s.points.map((p) => ({
      ts: tsOf(p.rated_at),
      value: p.value,
      label: s.label,
      color: s.color,
    })),
  );

  const platformLabel: Record<Platform, string> = {
    fide: 'FIDE',
    lichess: 'Lichess',
    chesscom: 'chess.com',
  };

  return (
    <ChartShell
      title={`Historique ELO ${platformLabel[platform]}`}
      ariaLabel={`Courbes ELO ${platformLabel[platform]} toutes cadences`}
      height={height}
      headerControls={null}
      legend={
        <div className="flex gap-3 mb-3 flex-wrap" data-testid="elo-history-legend">
          {series.map((s) => {
            const isHidden = !!hidden[s.cadence];
            const isDisabled = s.points.length === 0;
            return (
              <LegendButton
                key={s.cadence}
                id={s.cadence}
                label={s.label}
                color={s.color}
                hidden={isHidden}
                disabled={isDisabled}
                onToggle={() => toggleSeries(s.cadence)}
                testId={`legend-${s.cadence}`}
              />
            );
          })}
        </div>
      }
      visibleSeriesIds={visibleSeries.map((s) => s.cadence)}
      seriesForSvg={visibleSeries.map((s) => ({
        id: s.cadence,
        color: s.color,
        points: s.points,
        testId: `series-${s.cadence}`,
      }))}
      flatPoints={flatPoints}
      smooth={smooth}
      setSmooth={setSmooth}
      rangeId={rangeId}
      setRangeId={setRangeId}
      tooltip={tooltip}
      setTooltip={setTooltip}
      anyLoading={anyLoading}
      anyError={anyError}
    />
  );
}

// ===========================================================================
// Shell partagé : header + légende + SVG + range + smoothing
// ===========================================================================

interface SvgSeries {
  id: string;
  color: string;
  points: RatingHistoryPoint[];
  testId: string;
}

interface FlatPoint {
  ts: number;
  value: number;
  label: string;
  color: string;
}

interface ChartShellProps {
  title: string;
  ariaLabel: string;
  height: number;
  headerControls: React.ReactNode;
  legend: React.ReactNode;
  visibleSeriesIds: string[];
  seriesForSvg: SvgSeries[];
  flatPoints: FlatPoint[];
  smooth: boolean;
  setSmooth: (v: boolean) => void;
  rangeId: string;
  setRangeId: (id: string) => void;
  tooltip: TooltipState | null;
  setTooltip: (t: TooltipState | null) => void;
  anyLoading: boolean;
  anyError: boolean;
}

function ChartShell({
  title,
  ariaLabel,
  height,
  headerControls,
  legend,
  seriesForSvg,
  flatPoints,
  smooth,
  setSmooth,
  rangeId,
  setRangeId,
  tooltip,
  setTooltip,
  anyLoading,
  anyError,
}: ChartShellProps): JSX.Element {
  const W = 600;
  const H = height;
  const PADL = 38;
  const PADR = 16;
  const PADT = 16;
  const PADB = 28;

  const hasData = flatPoints.length > 0;
  const allValues = flatPoints.map((p) => p.value);
  const allTimestamps = flatPoints.map((p) => p.ts);

  const minV = hasData ? Math.floor((Math.min(...allValues) - 50) / 50) * 50 : 1600;
  const maxV = hasData ? Math.ceil((Math.max(...allValues) + 50) / 50) * 50 : 2200;
  const minT = hasData ? Math.min(...allTimestamps) : Date.now() - 30 * 86400_000;
  const maxT = hasData ? Math.max(...allTimestamps) : Date.now();
  const tRange = Math.max(1, maxT - minT);

  const xOf = (ts: number): number => PADL + ((ts - minT) / tRange) * (W - PADL - PADR);
  const yOf = (v: number): number => PADT + ((maxV - v) / (maxV - minV)) * (H - PADT - PADB);

  const gridValues: number[] = [];
  for (let v = Math.ceil(minV / 100) * 100; v <= maxV; v += 100) {
    gridValues.push(v);
  }

  function handleSvgMove(e: React.MouseEvent<SVGSVGElement>): void {
    if (!hasData) return;
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    let best: TooltipState | null = null;
    let bestDist = Infinity;
    for (const s of seriesForSvg) {
      for (const p of s.points) {
        const cx = xOf(tsOf(p.rated_at));
        const d = Math.abs(cx - px);
        if (d < bestDist) {
          bestDist = d;
          best = {
            x: cx,
            y: yOf(p.value),
            date: p.rated_at,
            value: p.value,
            seriesLabel:
              flatPoints.find((fp) => fp.ts === tsOf(p.rated_at) && fp.value === p.value)?.label
              ?? s.id,
            color: s.color,
          };
        }
      }
    }
    if (best && bestDist < 40) setTooltip(best);
    else setTooltip(null);
  }

  function handleSvgLeave(): void {
    setTooltip(null);
  }

  return (
    <div className="bg-ink-800 border border-ink-700 rounded-xl p-5" data-testid="elo-history-chart">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ChartLine size={18} weight="regular" color="var(--paper-50)" aria-hidden="true" />
          <h3 className="text-heading-3 text-paper-50 m-0">{title}</h3>
        </div>
        {headerControls}
      </div>

      {legend}

      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          role="img"
          aria-label={ariaLabel}
          onMouseMove={handleSvgMove}
          onMouseLeave={handleSvgLeave}
          data-testid="elo-history-svg"
        >
          {gridValues.map((v) => (
            <g key={v}>
              <line
                x1={PADL}
                y1={yOf(v)}
                x2={W - PADR}
                y2={yOf(v)}
                stroke="var(--ink-700)"
                strokeDasharray="2 4"
                opacity={0.5}
              />
              <text
                x={PADL - 6}
                y={yOf(v) + 3}
                fontFamily="JetBrains Mono"
                fontSize={9}
                fill="var(--ink-300)"
                textAnchor="end"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {v}
              </text>
            </g>
          ))}

          {seriesForSvg.map((s) => {
            const pts = s.points.map((p) => ({ x: xOf(tsOf(p.rated_at)), y: yOf(p.value) }));
            const d = buildPath(pts, smooth);
            return (
              <g key={s.id} data-testid={s.testId}>
                <path
                  d={d}
                  stroke={s.color}
                  strokeWidth={1.75}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {pts.map((p, i) => (
                  <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={i === pts.length - 1 ? 3.5 : 2}
                    fill={s.color}
                  />
                ))}
              </g>
            );
          })}

          {tooltip && (
            <g data-testid="elo-history-tooltip" pointerEvents="none">
              <line
                x1={tooltip.x}
                y1={PADT}
                x2={tooltip.x}
                y2={H - PADB}
                stroke="var(--ink-700)"
                strokeWidth={1}
                opacity={0.7}
              />
              <circle
                cx={tooltip.x}
                cy={tooltip.y}
                r={5}
                fill="none"
                stroke={tooltip.color}
                strokeWidth={1.5}
              />
            </g>
          )}
        </svg>

        {tooltip && (
          <div
            className="absolute pointer-events-none bg-ink-900 border border-ink-700 rounded-md px-2 py-1 font-mono text-[11px] tabular-nums text-paper-50"
            style={
              {
                left: `clamp(10%, ${(tooltip.x / W) * 100}%, 90%)`,
                top: 6,
                transform: 'translateX(-50%)',
              } satisfies CSSProperties
            }
            data-testid="elo-history-tooltip-label"
          >
            <span className="text-ink-300 mr-1">{tooltip.date}</span>
            <span style={{ color: tooltip.color }}>{tooltip.seriesLabel}</span>
            <span className="ml-1">{Math.round(tooltip.value)}</span>
          </div>
        )}

        {!hasData && !anyLoading && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            data-testid="elo-history-empty"
          >
            <p className="text-ink-300 italic text-body-sm text-center max-w-md px-4">
              Aucune donnée historique disponible. Saisis tes premiers ratings dans l'onglet FIDE.
            </p>
          </div>
        )}

        {anyLoading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-ink-300 font-mono text-body-sm">Chargement…</p>
          </div>
        )}

        {anyError && !anyLoading && (
          <div className="absolute top-2 right-2 text-coral-500 font-mono text-[11px]">
            Erreur de chargement
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
        <div className="flex gap-1" role="group" aria-label="Plage temporelle">
          {RANGES.map((r) => {
            const active = r.id === rangeId;
            return (
              <button
                key={r.id}
                onClick={() => setRangeId(r.id)}
                className={`px-2.5 py-1 text-body-sm font-mono uppercase tracking-wider rounded-md border transition-colors ${
                  active
                    ? 'border-paper-50 text-paper-50 bg-ink-900'
                    : 'border-ink-700 text-ink-300 hover:text-paper-50'
                }`}
                aria-pressed={active}
                data-testid={`range-${r.id}`}
              >
                {r.label}
              </button>
            );
          })}
        </div>
        <label className="flex items-center gap-1.5 text-body-sm font-mono text-ink-300 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={smooth}
            onChange={(e) => setSmooth(e.target.checked)}
            className="accent-acid-500"
            data-testid="elo-history-smooth-toggle"
          />
          <span>Lissage</span>
        </label>
      </div>
    </div>
  );
}

// ===========================================================================
// Légende — bouton toggle réutilisable (multi-plateforme et multi-cadence)
// ===========================================================================

interface LegendButtonProps {
  id: string;
  label: string;
  color: string;
  hidden: boolean;
  disabled: boolean;
  onToggle: () => void;
  testId: string;
}

function LegendButton({
  label,
  color,
  hidden,
  disabled,
  onToggle,
  testId,
}: LegendButtonProps): JSX.Element {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`flex items-center gap-1.5 px-2 py-1 rounded-md border border-ink-700 text-body-sm font-mono transition-colors ${
        disabled ? 'opacity-40 cursor-not-allowed' : 'hover:border-ink-600'
      } ${hidden ? 'opacity-50' : ''}`}
      aria-pressed={!hidden && !disabled}
      aria-label={`${label} ${hidden ? 'masqué' : 'affiché'}`}
      data-testid={testId}
    >
      {hidden || disabled ? (
        <EyeSlash size={14} color="var(--ink-300)" aria-hidden="true" />
      ) : (
        <Eye size={14} color={color} aria-hidden="true" />
      )}
      <span
        className="inline-block w-3 h-0.5"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      <span style={{ color: hidden || disabled ? 'var(--ink-300)' : 'var(--paper-50)' }}>
        {label}
      </span>
      {disabled && (
        <span className="text-ink-300 text-[10px] uppercase tracking-wider ml-1">—</span>
      )}
    </button>
  );
}
